import React from 'react';
import Icon from '../Icon';
import { IconNames } from '../Icon/iconMap';
import Typography from '../Typography';
import cn from '../Utils/cn';
import DefaultInput from './DefaultInput';
import DefaultLabel from './DefaultLabel';
import InputMessage from './InputMessage';

type Props = {
	id?: string;
	name?: string;
	type?: string;
	autoComplete?: string;
	state?: string;
	placeholder?: string;
	label?: string;
	labelMessage?: string;
	value?: number | string;
	message?: string;
	required?: boolean;
	multiline?: boolean;
	lines?: number;
	maskOptions?: any;
	disabled?: boolean;
	leftIcon?: IconNames;
	title?: string;
	className?: string;
	labelProps?: Omit<React.ComponentPropsWithoutRef<typeof DefaultLabel>, 'children'>;
	inputProps?: Omit<React.ComponentPropsWithoutRef<typeof DefaultInput>, 'children'>;
	messageProps?: Omit<React.ComponentPropsWithoutRef<typeof InputMessage>, 'children'>;
	setValue?: (value: string) => void;
	onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
};

const InputField: React.FC<Props> = React.memo (({
	id = 'input',
	name = id,
	type,
	autoComplete,
	state,
	placeholder,
	label,
	labelMessage,
	value,
	onChange,
	onBlur,
	message,
	required = true,
	multiline = false,
	lines = 3,
	maskOptions = {},
	disabled,
	setValue,
	labelProps = {},
	inputProps = {},
	messageProps = {},
	className,
	leftIcon = null,
	title,
}) => {
	
	return (
		<div
			className={cn(
				'my-[3.6rem]',
				{
					'opacity-50 pointer-events-none': disabled,
				},
				className,
			)}
		>
			{label != null ? (
				<DefaultLabel {...labelProps} htmlFor={id} required={required}>
					{labelMessage ? (
						<>
							{label}{' '}
							<Typography asEl="p" variant="body-3" className="mb-0">
								{labelMessage}
							</Typography>
						</>
					) : (
						label
					)}
				</DefaultLabel>
			) : null}

			{leftIcon == null ? (
				<DefaultInput
					{...(inputProps as any)}
					id={id}
					name={name}
					type={type}
					multiline={multiline}
					lines={lines}
					autoComplete={autoComplete}
					value={value}
					onChange={onChange}
					onBlur={onBlur}
					placeholder={placeholder}
					state={state}
					disabled={disabled}
					setValue={setValue}
					title={title}
					maskOpts={maskOptions}
				/>
			) : (
				<div className="relative">
					<Icon name={leftIcon} className="absolute left-[16px] top-1/2 transform -translate-y-1/2 text-lg text-base-grey-7" />

					<DefaultInput
						{...(inputProps as any)}
						id={id}
						name={name}
						type={type}
						multiline={multiline}
						lines={lines}
						autoComplete={autoComplete}
						value={value}
						onChange={onChange}
						onBlur={onBlur}
						placeholder={placeholder}
						state={state}
						maskOpts={maskOptions}
						disabled={disabled}
						setValue={setValue}
						hasLeftIcon={true}
						title={title}
					/>
				</div>
			)}

			<InputMessage {...messageProps} state={state}>
				{message}
			</InputMessage>
		</div>
	);
});

export default InputField;
